import {
    CombineOperator,
    CreatorProfileImageSize,
    DiscoveryLevel,
    GetCreatorProfileResponse,
    GetPointResponse,
    MapDrawer,
    SearchFilter,
    TopicSearchResult,
} from '../types';
import { GoogleTagEvent, logWithAmplitude, logWithGoogleTagAndTopicOrganization } from '../helpers/api/api';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';
import { getTopics, getTopicsforBackpack, getViewsForTopic } from '../helpers/api/topics-api';
import { isDefined, isValidString, undefinedIfEmpty } from '../helpers/utils';

import { Dialog } from '@material/mwc-dialog';
import { MapComponent } from '../components/map-component';
import { SettingDrawer } from '../components/mobile/setting-drawer';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import challengeData from '../helpers/state/challenge-data';
import { detectMobileViewport } from '../helpers/device';
import { getCreatorProfile } from '../helpers/api/creator-profile-api';
import { getMainPage } from '../helpers/page-name';
import { getQRCode } from '../helpers/urls';
import { marked } from 'marked';
import { onBuildMapClick } from '../helpers/build-new-topic';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';

@customElement('discover-details-page')
export class DiscoverDetailsPage extends LitElement {
    amplitude_callsite: string = 'Discover Details';



    @query('#addToMapModal')
    addToMapModal!: Dialog;

    @query('#addToMapDrawer')
    addToMapDrawer?: SettingDrawer;

    @query('#getRouteDrawer')
    getRouteDrawer?: SettingDrawer;

    @query('#getRouteModal')
    getRouteModal!: Dialog;

    @query('#mapComponent')
    mapComponentEl!: MapComponent;

    @property({ type: String })
    point_id?: string;

    @property({ type: String })
    shareLink: string | undefined;

    @property({ type: String })
    qrCodeLink: string | undefined;

    @property({ type: String })
    crowdsourceLink?: string;

    @property({ type: String })
    viewonlyLink?: string;

    @property({ type: String })
    viewType: string | undefined;

    @state()
    focusedPoints: GetPointResponse[] = [];

    @state()
    topics: TopicSearchResult[] = [];

    @state()
    topicViewCount: number = 0;

    @state()
    popularTopics: TopicSearchResult[] = [];

    @state()
    creatorProfile?: GetCreatorProfileResponse;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        const urlParts = window.location.href.split('#');
        const queryString = new URLSearchParams(urlParts[0].split('?')[1]);
        let point_id = queryString.get('point_id');

        if (!point_id && urlParts[1]) {
            const hashParams = new URLSearchParams(urlParts[1].split('?')[1]);
            point_id = hashParams.get('point_id');
        }

        this.point_id = point_id ?? undefined;

        await topicData.instantiateTopicData(
            this.amplitude_callsite,
            accountInfo.isAuthenticated,
            true,
            true,
            undefined,
            undefined,
            undefined,
            this.mapComponentEl
        );

        window.dispatchEvent(new CustomEvent('page-content-ready'));

        if (isDefined(topicData.topic)) {
            await pointCache.instantiatePointCache(
                topicData.topic,
                topicData.topicKey,
                false,
                true,
                challengeData.challenge ? challengeData.allCheckins : accountInfo.account_checkins,
            );
        }

        // Near by topics
        const searchFilter: SearchFilter = {};
        if (isDefined(topicData.topic)) {
            searchFilter.location_match = {
                location: topicData.topic.default_map_center,
                max_distance: 20000,
            };
            const search_text = topicData.name
                .trim()
                .toLocaleLowerCase()
                .replace(/[^0-9a-z ]/gi, '')
                .split(' ');
            searchFilter.keyword_search = {
                operator: CombineOperator.Or,
                keywords: search_text,
                sub_searches: [],
            };
        }
        this.popularTopics = (await getTopics(this.amplitude_callsite, searchFilter)) ?? [];
        this.popularTopics = this.popularTopics
            .filter((topic) => topic._id.$oid !== topicData.topicId)
            .slice(0, 4);

        // Get links
        if (topicData.topic?.is_addpointer === true) {
            if (topicData.discoveryLevel === 'addpoint_public') {
                this.crowdsourceLink = undefinedIfEmpty(topicData.swapUrlForOrgUrl(topicData.topic?.cc_keyless_link ?? ''));
            } else {
                this.crowdsourceLink = undefinedIfEmpty(topicData.swapUrlForOrgUrl(topicData.topic?.cc_addpoint_link ?? ''));
            }
        }
        if (topicData.discoveryLevel !== 'private') {
            this.viewonlyLink = undefinedIfEmpty(topicData.swapUrlForOrgUrl(topicData.topic?.cc_keyless_link ?? ''));
        } else {
            this.viewonlyLink = undefinedIfEmpty(topicData.swapUrlForOrgUrl(topicData.topic?.cc_read_link ?? ''));
        }

        // Grab View Type Text for Analytics
        if (topicData.topic?.is_addpointer === true) {
            this.viewType = 'Collaborator';
        } else if (topicData.isWriter) {
            this.viewType = 'Admin';
        } else {
            this.viewType = 'Viewer';
        }
        logWithAmplitude(this.amplitude_callsite, `Load ${this.viewType} View`, {
            topic_id: topicData.topicId,
        });

        // Share link
        await this.setupShareMap();

        // Creator profile
        if (topicData.creatorProfiles.length !== 0) {
            this.creatorProfile = await getCreatorProfile(
                this.amplitude_callsite,
                topicData.creatorProfiles[0].$oid
            );
        }

        // Metrics
        const telemetryEntries = await getViewsForTopic(
            this.amplitude_callsite,
            topicData.topicId,
            topicData.topicKey
        );
        this.topicViewCount = telemetryEntries
            .map((entry) => entry.value)
            .reduce((agg, v) => agg + v, 0);

        document.documentElement.style.setProperty('--custom-font', topicData.topic?.custom_font ?? 'Montserrat');
        this.style.setProperty('font-family', topicData.topic?.custom_font ?? 'Montserrat');

        logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.DISCOVER_DETAILS_PAGE_VIEW);
    }

    private async setupShareMap() {
        if (topicData.discoveryLevel === 'private') {
            if (topicData.topic?.is_addpointer === true) {
                this.shareLink = topicData.swapUrlForOrgUrl(topicData.topic.cc_addpoint_link ?? '');
                logWithAmplitude(this.amplitude_callsite, `Share Map Click`, {
                    topic_id: topicData.topicId,
                    share_type: 'collaborator',
                });
            } else {
                this.shareLink = topicData.swapUrlForOrgUrl(topicData.topic?.cc_read_link ?? '');
                logWithAmplitude(this.amplitude_callsite, `Share Map Click`, {
                    topic_id: topicData.topicId,
                    share_type: 'read only',
                });
            }
        } else if (topicData.discoveryLevel === 'read_public') {
            if (topicData.topic?.is_addpointer === true) {
                this.shareLink = topicData.swapUrlForOrgUrl(topicData.topic.cc_addpoint_link ?? '');
                logWithAmplitude(this.amplitude_callsite, `Share Map Click`, {
                    topic_id: topicData.topicId,
                    share_type: 'collaborator',
                });
            } else {
                this.shareLink = topicData.swapUrlForOrgUrl(topicData.topic?.cc_keyless_link ?? '');
                logWithAmplitude(this.amplitude_callsite, `Share Map Click`, {
                    topic_id: topicData.topicId,
                    share_type: 'keyless',
                });
            }
        } else {
            this.shareLink = topicData.swapUrlForOrgUrl(topicData.topic?.cc_keyless_link ?? '');
            logWithAmplitude(this.amplitude_callsite, `Share Map Click`, {
                topic_id: topicData.topicId,
                share_type: 'keyless',
            });
        }
        this.qrCodeLink =
            this.shareLink !== undefined
                ? await getQRCode(this.amplitude_callsite, this.shareLink)
                : '';
    }

    private getDescriptionString(description: string | undefined): string {
        if (isValidString(description)) {
            let parsedDescription = marked.parse(description ?? '');
            parsedDescription = parsedDescription.replaceAll('http://', '');
            parsedDescription = parsedDescription.replaceAll('https://', '');
            parsedDescription = parsedDescription.replaceAll('href="', 'href="https://');
            parsedDescription = parsedDescription.replaceAll('<a', '<a target="_blank"');

            return parsedDescription;
        }

        return '';
    }

    private openFormPage() {
        // get current link and just add view=form
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.set('view', 'form');
        window.open(currentUrl.toString(), '_blank');
    }

    private async onShowAddToMapModal(e: CustomEvent) {
        this.focusedPoints =  e.detail.points;
        if (detectMobileViewport()) {
            void this.addToMapDrawer?.open();
        } else {
            this.addToMapModal.open = true;
        }

        this.topics = await getTopicsforBackpack(this.amplitude_callsite);
    }

    render() {
        if (topicData.isReady && pointCache.isReady) {
            return html`
                <div class="discover__page-container">
                    <nav-bar type="discover"></nav-bar>
                    <div class="discover__center-content">
                        <div class="discover__header-wrapper">
                            <span>
                                <div class="discover__header">
                                    <h1 class="discover__heading">${topicData.name}</h1>
                                    <bookmark-map-button .topic=${topicData.topic} .onWhite=${true}>
                                    </bookmark-map-button>
                                </div>
                                <topic-metadata></topic-metadata>
                            </span>
                            ${isDefined(this.creatorProfile) && !detectMobileViewport()
                                ? html`<creator-profile-card
                                      .linkToCreatorProfile=${true}
                                      .showSocialCtas=${false}
                                      .imageSize=${CreatorProfileImageSize.SMALL}
                                      .showDescription=${false}
                                      .creatorProfile=${this.creatorProfile}
                                  >
                                  </creator-profile-card>`
                                : ''}
                        </div>

                        <div class="discover__map-section">
                            ${this.shareLink && !detectMobileViewport()
                                ? html`<social-share-buttons
                                      size="md"
                                      class="discover__share-map"
                                      .buttonsInColumn=${true}
                                      shareLink="${this.shareLink}"
                                      hideQrCodeButton
                                  >
                                  </social-share-buttons>`
                                : ''}
                            ${topicData.isReady
                                ? html`
                                ${topicData.topic?.hide_header !== true
                                    ? html`
                                    <map-header>
                                        <div
                                            class="headerPart"
                                            style="--context-menu-text-color: ${topicData.textColor}; display: flex; align-items: center; gap: 10px; flex-direction: row;"
                                        >
                                            ${topicData.topic?.translation_languages && topicData.topic?.translation_languages.length > 0 ? html`
                                                <language-selector></language-selector>
                                            ` : ''}
                                            ${topicData.topic?.hide_map_info_drawer !== true && !detectMobileViewport() ? html`
                                                <mwc-icon
                                                    style="cursor: pointer;"
                                                    @click=${async () => await topicData.mapComponent?.setMapDrawer(MapDrawer.MapInfo)}
                                                    >manage_search</mwc-icon>
                                            ` : ''}
                                            ${topicData.topic?.hide_bookmark_buttons !== true ? html`
                                                <bookmark-map-button
                                                    id="bookmarkFab"
                                                    .topic=${topicData.topic}
                                                >
                                                </bookmark-map-button>
                                            ` : ''}
                                            <context-menu
                                                type="command-center"
                                                amplitude_callsite=${this.amplitude_callsite}
                                                @on-signin=${() => {
                                                    if (topicData.mapComponent) {
                                                        topicData.mapComponent.signinModalComponent.open = true;
                                                    }
                                                }}
                                            >
                                            </context-menu>
                                        </div>
                                    </map-header>
                                        `
                                    : ''}
                                <div class="discover__map">
                                    <map-component
                                        id="mapComponent"
                                        .isDiscoveryView=${true}
                                        .focused_point_id=${this.point_id}
                                        .election="${topicData.electionHappeningNow}"
                                        @on-show-add-to-map="${this.onShowAddToMapModal}"
                                        >
                                    </map-component>
                                  </div>`
                                : ''}
                            <div class="discover__view-buttons">
                                <div
                                    @click="${() => window.open(this.viewonlyLink ?? '')}"
                                    class="discover__view-button discover__view-button--full-view"
                                >
                                    View Full Screen
                                </div>
                                ${
                                topicData.discoveryLevel === 'addpoint_public'
                                    ? html`<div
                                            @click="${this.openFormPage}"
                                            class="discover__view-button discover__view-button--contribute-map"
                                        >
                                            Contribute to Map
                                        </div>`
                                    : ''}
                                ${!(topicData.topic?.disable_build_your_own === true) &&
                                topicData.discoveryLevel !== DiscoveryLevel.Private &&
                                pointCache.hasFilterApplied
                                    ? html` <div
                                        @click="${async () =>
                                            await onBuildMapClick(
                                                accountInfo,
                                                topicData,
                                                pointCache,
                                                getMainPage().errorToastElement,
                                                this.amplitude_callsite
                                            )}"
                                        class="discover__view-button discover__view-button--remix-map"
                                    >
                                        Remix Map
                                    </div>`
                                : ''}
                            </div>
                        </div>
                        <div class="discover__content-section">
                            <div class="discover__content-section--left">
                                ${isDefined(this.creatorProfile) && detectMobileViewport()
                                    ? html`<creator-profile-card
                                          .linkToCreatorProfile=${true}
                                          .showSocialCtas=${false}
                                          .imageSize=${CreatorProfileImageSize.SMALL}
                                          .showDescription=${false}
                                          .creatorProfile=${this.creatorProfile}
                                      >
                                      </creator-profile-card>`
                                    : ''}
                                <div class="discover__description-title-wrapper">
                                    <h2 class="discover__description-title">
                                        Take a closer look at
                                        <span class="discover__description-title--orange"
                                            >${topicData.name}</span
                                        >
                                    </h2>
                                    ${isDefined(topicData.topic?.description) &&
                                    topicData.topic?.description !== ''
                                        ? html`<span class="discover__description">
                                              ${new DOMParser().parseFromString(
                                                  this.getDescriptionString(
                                                      topicData.topic?.description
                                                  ),
                                                  'text/html'
                                              ).body}
                                          </span>`
                                        : ''}
                                </div>
                                <expanded-point-list class="discover__point-list">
                                </expanded-point-list>
                            </div>
                            <div class="discover__content-section--right">
                                ${isDefined(this.creatorProfile) && !detectMobileViewport()
                                    ? html`<creator-profile-card
                                          .showSocialCtas=${false}
                                          .imageSize=${CreatorProfileImageSize.MEDIUM}
                                          .useVerticalLayout=${true}
                                          .creatorProfile=${this.creatorProfile}
                                      >
                                      </creator-profile-card>`
                                    : ''}
                                <div class="discover__popular-topics-wrapper">
                                    ${this.popularTopics.length > 0
                                        ? html`<h3 class="discover__topics-heading">
                                                  More Proxi Maps
                                              </h3>
                                              ${this.popularTopics.map((topic) => {
                                                  return html`<backpack-card
                                                      class="topic"
                                                      .topic=${topic}
                                                      ?goToDetailPage=${true}
                                                      ?hideViewerType=${true}
                                                      ?hideMetadata=${true}
                                                  ></backpack-card>`;
                                              })}`
                                        : ''}
                                    <div></div>
                                </div>
                            </div>
                        </div>
                        ${
                            detectMobileViewport()
                                ? html`
                                <setting-drawer id="addToMapDrawer" isExpanded="${true}">
                                    <add-point-to-map-component
                                        .points=${this.focusedPoints}
                                        .topics=${this.topics}
                                        @close-modal="${() => {
                                            void this.addToMapDrawer?.close();
                                        }}"
                                    >
                                    </add-point-to-map-component>
                                </setting-drawer>`
                                : html`
                                <mwc-dialog id="addToMapModal" class="dialog" hideActions>
                                    <modal-dialog size="md" @close="${() => this.addToMapModal.close()}" >
                                        <add-point-to-map-component
                                            slot="body"
                                            .points=${this.focusedPoints}
                                            .topics=${this.topics}
                                            @close-modal="${() => {
                                                this.addToMapModal.close();
                                            }}"
                                        ></add-point-to-map-component>
                                    </modal-dialog>
                                </mwc-dialog>`
                        }
                        ${
                            detectMobileViewport()
                                ? html`
                                <setting-drawer id="getRouteDrawer" isExpanded="${true}">
                                    <get-route-component
                                        .points=${this.focusedPoints}
                                        @close-modal="${() => {
                                            void this.getRouteDrawer?.close();
                                        }}"
                                    >
                                    </get-route-component>
                                </setting-drawer>`
                                : html`
                                <mwc-dialog id="getRouteModal" class="dialog" hideActions>
                                    <modal-dialog size="md" @close="${() => this.getRouteModal.close()}" >
                                        <get-route-component
                                            slot="body"
                                            .points=${this.focusedPoints}
                                            @close-modal="${() => {
                                                this.getRouteModal.close();
                                            }}"
                                        ></get-route-component>
                                    </modal-dialog>
                                </mwc-dialog>`
                        }

                        <error-toast id="errorToast"></error-toast>
                    </div>
                </div>
            `;
        }
    }

    static get styles() {
        return css`
            .discover__metadata-wrapper {
                display: flex;
            }

            .discover__header-wrapper {
                margin-bottom: 40px 20px 24px;
            }

            .discover__backpack-card {
                margin-top: 5px;
            }

            .discover__heading {
                margin-bottom: 4px;
            }

            .discover__description-title {
                margin-left: 6px;
            }

            .discover__description {
                line-height: 20px;
            }

            .discover__description-title--orange {
                color: var(--main-invalid-color);
            }

            .discover__header {
                display: flex;
                align-items: self-end;
            }

            .discover__map-section {
                position: relative;
                height: 60vh;
                display: flex;
                flex-direction: column;
            }

            .discover__map {
                position: relative;
                width: 100%;
                height: 100%;
                overflow: hidden;
            }

            .discover__share-map {
                margin-right: 20px;
                position: fixed;
                top: 20%;
                left: 10px;
            }

            .discover__view-buttons {
                position: absolute;
                bottom: 0;
                display: flex;
                left: 50%;
                transform: translateX(-50%);
                z-index: 4;
            }

            .discover__view-button {
                padding: 8px;
                color: var(--theme-white-color);
                font-size: 12px;
                font-weight: 700;
                border-radius: 10px 10px 0 0;
                cursor: pointer;
                margin: 0 10px;
            }

            .discover__view-button--full-view {
                background: var(--main-primary-color);
            }

            .discover__view-button--remix-map {
                background: var(--main-featured-color);
            }

            .discover__view-button--contribute-map {
                background: var(--main-success-color);
            }

            .discover__topics-heading {
                font-size: 18px;
                font-weight: 700;
            }

            .discover__page-container {
                background: var(--theme-lightest-color);
            }

            .discover__popular-topics-wrapper {
                display: flex;
                flex-direction: column;
                gap: 10px;
            }

            @media only screen and (min-width: 551px) {
                .discover__center-content {
                    margin: auto;
                    width: 70%;
                }

                .discover__content-section--left {
                    flex: 5;
                    padding-right: 40px;
                    margin-bottom: 40px;
                    max-width: 70%;
                }

                .discover__content-section--right {
                    flex: 3;
                    overflow: hidden;
                }

                .discover__content-section {
                    display: flex;
                    margin: 40px 20px;
                }

                .discover__header-wrapper {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-end;
                    margin-bottom: 20px;
                    margin-top: 40px;
                }
            }

            @media only screen and (max-width: 550px) {
                .discover__center-content {
                    margin: 20px;
                    overflow-y: scroll;
                }

                .discover__view-buttons {
                    position: fixed;
                    bottom: 0;
                    display: flex;
                    left: 50%;
                    transform: translateX(-45%);
                    z-index: 4;
                    width: 95vw;
                }

                .discover__content-section {
                    margin-top: 20px;
                    padding-bottom: 30px;
                }

                .discover__view-button {
                    width: auto;
                    padding: 7px 16px;
                    font-size: 12px;
                    font-weight: 700;
                    margin: 0 5px;
                }

                .discover__header {
                    display: flex;
                    align-items: center;
                }

                .discover__heading {
                    margin-bottom: 4px;
                    margin-top: 4px;
                }

                .discover__map-section {
                    margin-top: 20px;
                    height: 60vh;
                }

                #addpointDrawer {
                    --bottom-height: 0px;
                    --bottom-height-half: 0px;
                }
            }
        `;
    }
}
